<template>
  <HKLModal
    ref="modal"
    size="large"
    dialogStyleOpt="stickyTop"
    :closable="true"
    @open="openHandler"
  >
    <div class="submission-email-subject">
      <label>{{ $t('eCircular.subject') }}</label>
      <div>
        {{
          `${$t('tenantProjects.postFitout.titleWithNumber', [currentSubmission.order ? currentSubmission.order + 1 : ''])} - ${currentTenantProject.projectName}`
        }}
      </div>
    </div>
    <EmailEditFields v-if="canSend" />
    <CommentsPreviewBody
      :isBatchesView="false"
      :activeBatch="activeBatch"
    />
    <TenantSubmissionDocuments
      :files.sync="files"
      :sourceId="(currentSubmission || {}).id || ''"
      :canEdit="false"
    />
    <div
      v-if="canSend"
      class="row align-right nav_btn"
    >
      <HKLButton
        v-hkldreset
        color="darkblue"
        action="save"
        @click="saveReceivers()"
      />
      <HKLButton
        v-hkldreset
        color="orange"
        :action="hasSend ? 'resend' : 'send'"
        @click="sendReceivers()"
      />
    </div>
    <PostFitOutCommentHistory />
    <PostFitOutReviewerHistory />
    <template slot="footer">
      <div />
    </template>
  </HKLModal>
</template>

<script>
import { sendEmail } from '@/api/postman/request'
import { processDuplicateEmails } from '@/api/postman/util'
import { updatePostFitoutSubmission } from '@/api/tenantProjects/postFitout.request'
import HKLButton from '@/controls/Button/HKLButton'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { useStoreActions, useStoreGetters } from '@/store'
import { EmailTypes } from '@/utils/consts'
import lodash from 'lodash'
import { computed, getCurrentInstance, inject, ref, unref, watchEffect } from 'vue-demi'
import EmailEditFields from '../EmailEditFields'
import { DEPENDENCY_KEYS } from '../consts'
import { canSendInPreviewModel, useEmailTool } from '../hook'
import CommentsPreviewBody from '../submissions/CommentsPreviewBody'
import TenantSubmissionDocuments from '../submissions/TenantSubmissionDocuments'
import previewSubmissionModalMixin from '../submissions/previewSubmissionModalMixin'
import PostFitOutCommentHistory from './PostFitOutCommentHistory'
import PostFitOutReviewerHistory from './PostFitOutReviewerHistory'
import { PF_HISTORY_ACTION_TYPE, PF_HISTORY_TYPE } from './consts'
import { useComputedDescription, useProjectId, useProjectPostFitOuts, useUnderReviewPostFitout } from './hook'
import { buildPostFitoutHistory, buildUpdatePostFitoutRequest } from './util'

/// <reference types="vue-demi" />

export default {
  components: {
    HKLModal,
    HKLButton,
    EmailEditFields,
    CommentsPreviewBody,
    TenantSubmissionDocuments,
    PostFitOutReviewerHistory,
    PostFitOutCommentHistory,
  },
  mixins: [previewSubmissionModalMixin],
  setup () {
    const { proxy } = getCurrentInstance()
    const projectId = useProjectId()
    const guidelines = ref([])
    const { to, cc, bcc, init: emailInit } = useEmailTool()
    const currentSubmission = inject(DEPENDENCY_KEYS.CURRENT_SUBMISSION)
    const files = ref([])
    const modals = useRefModal()
    const { loadProjectPostFitoutSubmissions } = useStoreActions('postFitoutSubmission', ['getUnderReviewSubmission', 'loadProjectPostFitoutSubmissions'])
    const {
      currentTenantProject,
      hasPendingSubmission,
    } = useStoreGetters('tenantProject', [
      'currentTenantProject',
      'hasPendingSubmission',
    ])
    const { currentUserEmail } = useStoreGetters('user', ['currentUserEmail'])
    const canSend = canSendInPreviewModel()
    /** @type {ComputedRef<string>} */
    const openingDescription = useComputedDescription(currentSubmission, 'openingDescription')
    /** @type {ComputedRef<string>} */
    const closingDescription = useComputedDescription(currentSubmission, 'closingDescription')
    /** @type {ComputedRef<string>} */
    const description = useComputedDescription(currentSubmission, 'description')
    const activeBatch = computed(() => {
      return {
        techComment: {
          id: unref(currentSubmission).id,
          greetingDescription: unref(openingDescription),
          description: unref(description),
          closingDescription: unref(closingDescription),
          comments: unref(guidelines),
        },
        order: unref(currentSubmission).order,
      }
    })
    const hasSend = computed(() => {
      return (unref(currentSubmission).histories || []).some(h => h.actionType === PF_HISTORY_ACTION_TYPE.SEND)
    })

    watchEffect(() => {
      const underReviewGuidelines = lodash.cloneDeep(lodash.get(unref(currentSubmission), 'comments.guidelines', []))
      guidelines.value = underReviewGuidelines
    })
    const openHandler = async () => {
      if (!unref(canSend)) return
      await emailInit()
      if (unref(hasSend) && !cc.value.includes(currentUserEmail.value)) {
        cc.value = `${cc.value};${currentUserEmail.value}`
      }
    }
    const activedTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    const getCurrentReceivers = () => {
      return {
        to: unref(to),
        cc: unref(cc),
        bcc: unref(bcc),
      }
    }
    const createCurrentReceiversHistory = () => {
      return buildPostFitoutHistory(
        PF_HISTORY_TYPE.SUBMISSION,
        PF_HISTORY_ACTION_TYPE.RECEIVERS,
        {
          emailReceivers: getCurrentReceivers(),
        },
      )
    }
    const save = async (reqPayload = {}, addHistories = []) => {
      const { id, lock } = unref(currentSubmission)
      const requestBody = buildUpdatePostFitoutRequest({
        lock,
        ...reqPayload,
      }, {
        addHistory: addHistories,
      })
      await updatePostFitoutSubmission(id, requestBody)
      await refreshPostFitOuts()
    }
    const refreshPostFitOuts = async () => {
      // refresh whole Project's Post fit out
      await loadProjectPostFitoutSubmissions(projectId)
      const { id } = unref(currentSubmission)
      const underReviewItem = useUnderReviewPostFitout()
      const postFitouts = useProjectPostFitOuts()
      if (id === underReviewItem.id) {
        currentSubmission.value = unref(underReviewItem)
      } else {
        currentSubmission.value = unref(postFitouts).find((item) => item.id === id)
      }
    }
    const validateReceivers = () => {
      const formElem = unref(modals.modal).$el
      fusionInputCheckUtil.validateAllFields(undefined, formElem)
      return fusionInputCheckUtil.isAllFieldsValid(formElem)
    }
    const saveReceivers = async () => {
      if (!validateReceivers()) return
      await save({}, [createCurrentReceiversHistory()])
    }
    const sendReceivers = async () => {
      if (!validateReceivers()) return
      await send()
      modals.close()
    }
    const send = async () => {
      const actionType = !unref(hasSend) ? PF_HISTORY_ACTION_TYPE.SEND : PF_HISTORY_ACTION_TYPE.RESEND
      const extMetaData = {
        to: proxy.splitEmails(unref(to)),
        cc: proxy.splitEmails(unref(cc)),
        bcc: proxy.splitEmails(unref(bcc)),
        docIds: unref(files).map(file => file.id),
        action: actionType,
        type: unref(activedTabType),
        submission: lodash.cloneDeep(unref(currentSubmission)),
      }
      const addHistories = [
        buildPostFitoutHistory(PF_HISTORY_TYPE.SUBMISSION, actionType, {
          emailReceivers: getCurrentReceivers(),
        }),
      ]

      const reqPayload = unref(hasSend) ? {} : { completedDate: Date.now() }
      await save(reqPayload, addHistories)
      processDuplicateEmails(extMetaData.to, extMetaData.cc, extMetaData.bcc)
      await sendEmail(EmailTypes.tenantProjects.completePostfitout, extMetaData, false)
    }
    return {
      ...modals,
      canSend,
      hasSend,
      files,
      openHandler,
      guidelines,
      currentSubmission,
      currentTenantProject,
      hasPendingSubmission,
      activeBatch,
      get: lodash.get,
      saveReceivers,
      sendReceivers,
    }
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/email_preview';

.nav_btn {
  width: 100%;
  & > * {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
}
</style>
